// ══════════════════════════════════════════════════════════════════════════════════════════════
//  LLEVAR A UN APARTADO CONCRETO DE UNA PANTALLA, COMO UN ANCLA (4-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  El dueño, literal: «las tareas del onboarding, cuando pulsas el botón para que te lleve a donde se debe configurar,
//  si es una pestaña con muchas configuraciones te debería llevar exactamente al punto donde se encuentra ese
//  desplegable y dejarlo abierto, como si fuera un ancla».
//
//  EL MECANISMO, COMÚN A TODAS LAS PANTALLAS:
//   · LA DIRECCIÓN DICE EL APARTADO: `?abrir=<id>` (o `#<id>`; y en Ajustes la marca de antes, `?seccion=<x>`, que es el
//     apartado `seccion-<x>`). Es compartible: pegada en otro navegador lleva al mismo sitio.
//   · CADA APARTADO LLEVA `data-apartado="<id>"` en su caja (un desplegable de Ajustes, una pestaña, un bloque).
//   · EL DESPLEGABLE O LA PESTAÑA SE ABRE SOLO si la dirección lo pide (`esElApartadoPedido(id)` / `apartadoPedido()`):
//     al montarse ya nace abierto, y si la dirección cambia estando ya en la pantalla, se abre en ese momento.
//   · LA APLICACIÓN LO TRAE A LA VISTA Y LO RESALTA UN MOMENTO (`irAlApartado`, desde components/Layout y AdminLayout
//     cada vez que cambia la dirección). ESPERA a que exista: muchas pantallas pintan «Cargando…» y el apartado aparece
//     cuando llegan sus datos. En el móvil, igual (y sin animación si el sistema pide reducir el movimiento).
//
//  POR QUÉ ES UN `utils/` SIN REACT Y LEE `window.location`: las guardias montan muchas de estas pantallas con un doble
//  por CADA import, y un import nuevo de un hook las reventaría al cargar (ver pages/ControlHorarioAdmin.jsx). Un
//  `utils/*.js` se carga de verdad en esas guardias. Lo mide backend/routes/primeros-pasos-lleva-al-apartado.test.js.
export const PARAM = 'abrir'
export const ATRIBUTO = 'data-apartado'
export const ATRIBUTO_RESALTE = 'data-apartado-resaltado'
// Empieza por una letra: `?abrir=123` ya lo usan otras pantallas para abrir una ficha por su número (Cirugías, Pre-registros),
// y eso no es un apartado.
const FORMA = /^[a-z][a-z0-9_-]{0,60}$/i

const limpio = (v) => {
  const t = String(v == null ? '' : v).trim()
  return FORMA.test(t) ? t : null
}

// El apartado que pide una dirección (su `search` y su `hash`), o null.
export function apartadoDeLaDireccion(search, hash) {
  let q = null
  try { q = new URLSearchParams(String(search || '')) } catch { q = null }
  const abrir = q && limpio(q.get(PARAM))
  if (abrir) return abrir
  const h = limpio(String(hash || '').replace(/^#/, ''))
  if (h) return h
  const s = q && limpio(q.get('seccion'))
  return s ? 'seccion-' + s : null
}

// El que pide la dirección de AHORA. Se lee en cada pintado: si se navega a la misma pantalla con otro apartado, el
// componente no se vuelve a montar y un estado leído solo al montar no se enteraría.
export function apartadoPedido() {
  try { return apartadoDeLaDireccion(window.location.search, window.location.hash) } catch { return null }
}
export const esElApartadoPedido = (id) => !!id && apartadoPedido() === id

// Una ruta con su apartado: conApartado('/ajustes?area=clinica', 'seccion-recetas') → '/ajustes?area=clinica&abrir=seccion-recetas'.
export function conApartado(ruta, id) {
  const clave = limpio(id)
  const r = String(ruta || '')
  if (!clave) return r
  const [sinHash, hash] = r.split('#')
  return sinHash + (sinHash.includes('?') ? '&' : '?') + PARAM + '=' + encodeURIComponent(clave) + (hash != null ? '#' + hash : '')
}

const reducido = (w) => {
  try { return !!(w && w.matchMedia && w.matchMedia('(prefers-reduced-motion: reduce)').matches) } catch { return false }
}

// Busca el apartado `id` en la página (si todavía no está, lo vuelve a buscar cada `cadaMs` hasta `esperaMs`), lo trae a la
// vista y lo resalta `resalteMs`. Devuelve una función que lo cancela (al navegar a otro sitio antes de que aparezca).
// `doc`/`win` son para las guardias.
export function irAlApartado(id, { doc, win, esperaMs = 10000, cadaMs = 120, resalteMs = 2400 } = {}) {
  const d = doc || (typeof document !== 'undefined' ? document : null)
  const w = win || (typeof window !== 'undefined' ? window : null)
  const clave = limpio(id)
  if (!d || !clave) return () => {}
  const inicio = Date.now()
  let hecho = false, tBuscar = null, tResalte = null
  const paso = () => {
    if (hecho) return
    let el = null
    try { el = d.querySelector('[' + ATRIBUTO + '="' + clave + '"]') } catch { el = null }
    if (!el) {
      if (Date.now() - inicio < esperaMs) tBuscar = setTimeout(paso, cadaMs)
      return
    }
    hecho = true
    try { el.scrollIntoView({ block: 'start', behavior: reducido(w) ? 'auto' : 'smooth' }) } catch { /* sin desplazamiento */ }
    try { el.setAttribute(ATRIBUTO_RESALTE, '') } catch { /* sin resalte */ }
    tResalte = setTimeout(() => { try { el.removeAttribute(ATRIBUTO_RESALTE) } catch { /* ya no está */ } }, resalteMs)
  }
  // Un respiro antes de la primera búsqueda: el desplegable se abre en el mismo pintado y su contenido llega justo después.
  tBuscar = setTimeout(paso, 60)
  return () => { hecho = true; clearTimeout(tBuscar); clearTimeout(tResalte) }
}

// Lo que hacen las dos cabeceras de la aplicación cada vez que cambia la dirección.
export function alCambiarLaDireccion(opciones) {
  const id = apartadoPedido()
  return id ? irAlApartado(id, opciones) : undefined
}
